import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { cn } from '@plato/ui/util/classes';
import { CalendarIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SubordinatesSelect } from '../../shared/components/form-items-with-fetch/subordinates-select.tsx';
import type { EventCreateDialogProps } from './event-create-dialog.tsx';
import { EventCreateDialog } from './event-create-dialog.tsx';
import { EventsOverviewNavigation } from './events-overview-navigation.tsx';
import type { ViewMode } from './events-view-mode-selector.tsx';
import { OpenRouteInMapsButton } from './open-route-in-maps-button.tsx';
import { ViewingUserBanner } from './viewing-user-banner.tsx';

export type Props = {
    day: string;
    viewMode: ViewMode;
    className?: string;
    selectedOwnerId: string;
    onChangedDay: (day: string) => void;
    onViewModeChange: (mode: ViewMode) => void;
    onOwnerChange?: (ownerId: string | null) => void;
};

export function EventsOverviewHeader({
    className,
    day,
    viewMode,
    selectedOwnerId,
    onChangedDay,
    onViewModeChange,
    onOwnerChange,
}: Props) {
    const { t } = useTranslation();
    const { userId } = useAppConfig();

    return (
        <div className={cn('flex flex-col gap-3', className)}>
            <PageHeader
                icon={CalendarIcon}
                subTitle={t('page_sub_title__events')}
                actions={
                    <>
                        <OpenRouteInMapsButton
                            day={day}
                            ownerId={selectedOwnerId}
                            disabled={viewMode !== 'day'}
                        />
                        {onOwnerChange && (
                            <SubordinatesSelect
                                value={selectedOwnerId}
                                onValueChange={onOwnerChange}
                                placeholder={t('select_team_member')}
                                className="w-60"
                            />
                        )}
                        <Button
                            variant="primary"
                            icon={PlusIcon}
                            onClick={() => {
                                void DialogStateManager.show(EventCreateDialog, {
                                    date: day,
                                    analyticsSource: 'calendar',
                                } satisfies EventCreateDialogProps);
                            }}
                            disabled={selectedOwnerId !== userId}
                        >
                            {t('page_header__new_event', { ns: 'events' })}
                        </Button>
                    </>
                }
                title={t('page_title__events')}
            />
            <EventsOverviewNavigation
                day={day}
                viewMode={viewMode}
                onChangedDay={onChangedDay}
                onViewModeChange={onViewModeChange}
            />
            {onOwnerChange && (
                <ViewingUserBanner
                    ownerId={selectedOwnerId}
                    onBackToMyCalendar={() => {
                        onOwnerChange(null);
                    }}
                />
            )}
        </div>
    );
}
